import _ from 'lodash'
import {
  GET_USER_STARTED,
  GET_USER_COMPLETED,
  GET_USER_FAILED,

  LIST_ROLE_ASSIGNMENTS_STARTED,
  LIST_ROLE_ASSIGNMENTS_COMPLETED,
  LIST_ROLE_ASSIGNMENTS_FAILED,

  ASSIGN_ROLE_STARTED,
  ASSIGN_ROLE_COMPLETED,
  ASSIGN_ROLE_FAILED,

  UNASSIGN_ROLE_STARTED,
  UNASSIGN_ROLE_COMPLETED,
  UNASSIGN_ROLE_FAILED,

  LOCK_USER_STARTED,
  LOCK_USER_COMPLETED,
  LOCK_USER_FAILED,

  CHANGE_PASSWORD_STARTED,
  CHANGE_PASSWORD_COMPLETED,
  CHANGE_PASSWORD_FAILED,

  CLEAR_STATES
} from '@/store/types'
import { marshalRole, marshalUser } from '@/utils/marshal'

import contactsList from './user_details/contacts_list'
import oauthFactorsList from './user_details/oauth_factors_list'
import logsList from './user_details/logs_list'
import updateUserForm from './user_details/update_user_form'
import security from './user_details/security'
import devicesList from './user_details/devices_list'
import metadata from './user_details/metadata'

function getDefaultState () {
  return {
    user: {},
    roleAssignments: [],

    loading: false,
    error: undefined,

    actionLoading: false,
    actionError: undefined
  }
}

export default {
  namespaced: true,
  modules: {
    contactsList,
    oauthFactorsList,
    logsList,
    updateUserForm,
    security,
    devicesList,
    metadata
  },

  state: getDefaultState(),
  getters: {
    isMFAEnabled (state) {
      return state.user.smsAuthentication || state.user.totpAuthentication
    }
  },

  mutations: {
    // Get user
    [GET_USER_STARTED] (state, id) {
      state.loading = true
      state.user.id = id
    },
    [GET_USER_COMPLETED] (state, user) {
      state.user = marshalUser(user)
      state.loading = false
      state.error = undefined
    },
    [GET_USER_FAILED] (state, err) {
      state.loading = false
      state.error = err
    },

    // List roles
    [LIST_ROLE_ASSIGNMENTS_STARTED] (state) {
      state.loading = true
    },
    [LIST_ROLE_ASSIGNMENTS_COMPLETED] (state, payload) {
      const { roleAssignments, isMarshalNeeded } = payload
      if (isMarshalNeeded) {
        state.roleAssignments = _.cloneDeep(roleAssignments.map(marshalRole))
      } else {
        state.roleAssignments = _.cloneDeep(roleAssignments)
      }
      state.loading = false
      state.error = undefined
    },
    [LIST_ROLE_ASSIGNMENTS_FAILED] (state, err) {
      state.loading = false
      state.error = err
    },

    // Assign role
    [ASSIGN_ROLE_STARTED] (state) {
      state.actionLoading = true
    },
    [ASSIGN_ROLE_COMPLETED] (state) {
      state.actionLoading = false
      state.actionError = undefined
    },
    [ASSIGN_ROLE_FAILED] (state, err) {
      state.actionLoading = false
      state.actionError = err
    },

    // Unassign role
    [UNASSIGN_ROLE_STARTED] (state, roleAssignmentId) {
      const roleAssignmentIndex = state.roleAssignments.findIndex(roleAssignment => roleAssignment.id === roleAssignmentId)
      if (roleAssignmentIndex === -1) throw new Error('role assignment not found')
      state.roleAssignments[roleAssignmentIndex].unassigning = true
    },
    [UNASSIGN_ROLE_COMPLETED] (state, roleAssignmentId) {
      state.roleAssignments = state.roleAssignments.filter(roleAssignment => roleAssignment.id !== roleAssignmentId)
      state.actionError = undefined
    },
    [UNASSIGN_ROLE_FAILED] (state, payload) {
      const { roleAssignmentId, err } = payload
      const roleAssignmentIndex = state.roleAssignments.findIndex(roleAssignment => roleAssignment.id === roleAssignmentId)
      if (roleAssignmentIndex === -1) {
        state.actionError = new Error('role assignment not found')
      } else {
        state.roleAssignments[roleAssignmentIndex].unassigning = false
        state.actionError = err
      }
    },

    // Lock user
    [LOCK_USER_STARTED] (state) {
      state.actionLoading = true
    },
    [LOCK_USER_COMPLETED] (state) {
      state.actionLoading = false
      state.actionError = undefined
    },
    [LOCK_USER_FAILED] (state, err) {
      state.actionLoading = false
      state.actionError = err
    },

    // Change password
    [CHANGE_PASSWORD_STARTED] (state) {
      state.actionLoading = true
    },
    [CHANGE_PASSWORD_COMPLETED] (state) {
      state.actionLoading = false
      state.actionError = undefined
    },
    [CHANGE_PASSWORD_FAILED] (state, err) {
      state.actionLoading = false
      state.actionError = err
    },

    // Clear states
    [CLEAR_STATES] (state) {
      Object.assign(state, getDefaultState())
    }
  },
  actions: {
    async get ({ commit, rootState: { client = undefined } }, id) {
      try {
        commit(GET_USER_STARTED, id)
        const user = await client.managementClient.getUser(id)
        commit(GET_USER_COMPLETED, user)
      } catch (err) {
        commit(GET_USER_FAILED, err)
      }
    },
    async listRole ({ commit, rootState: { client = undefined } }, userId) {
      try {
        commit(LIST_ROLE_ASSIGNMENTS_STARTED)
        const roleAssignments = await client.managementClient.listRoleAssignments(userId)
        commit(LIST_ROLE_ASSIGNMENTS_COMPLETED, {
          // `roleAssignments` can be undefined, provide to be empty array if such case
          roleAssignments: roleAssignments || [],
          isMarshalNeeded: true
        })
      } catch (err) {
        commit(LIST_ROLE_ASSIGNMENTS_FAILED, err)
      }
    },
    async assign ({ commit, dispatch, rootState: { client = undefined, management } }, roleAssignmentId) {
      try {
        commit(ASSIGN_ROLE_STARTED)
        const { id: userId } = management.userDetails.user
        await client.managementClient.assignRole(userId, roleAssignmentId)
        commit(ASSIGN_ROLE_COMPLETED)
        await dispatch('listRole', userId)
      } catch (err) {
        commit(ASSIGN_ROLE_FAILED, err)
      }
    },
    async unassign ({ commit, rootState: { client = undefined, management } }, roleAssignmentId) {
      try {
        commit(UNASSIGN_ROLE_STARTED, roleAssignmentId)
        const { id: userId } = management.userDetails.user
        await client.managementClient.unassignRole(userId, roleAssignmentId)
        commit(UNASSIGN_ROLE_COMPLETED, roleAssignmentId)
      } catch (err) {
        commit(UNASSIGN_ROLE_FAILED, { roleAssignmentId, err })
      }
    },
    async lock ({ commit, rootState: { client = undefined, management } }, payload) {
      try {
        commit(LOCK_USER_STARTED)
        const { id: userId } = management.userDetails.user
        const { isLocked, lockInDays, description } = payload
        const updatedUser = await client.managementClient.updateUserLock(userId, isLocked, lockInDays, description)
        commit(GET_USER_COMPLETED, updatedUser)
        commit(LOCK_USER_COMPLETED)
      } catch (err) {
        commit(LOCK_USER_FAILED, err)
      }
    },
    async changePassword ({ commit, rootState: { client = undefined, management } }, payload) {
      try {
        const { id: userId } = management.userDetails.user
        const { newPassword, confirmNewPassword } = payload
        commit(CHANGE_PASSWORD_STARTED)
        if (newPassword === '') throw new Error('the new password should not be empty')
        // TODO: password meter checking
        if (newPassword !== confirmNewPassword) throw new Error('passwords are not equal')
        await client.managementClient.changePassword(userId, newPassword)
        commit(CHANGE_PASSWORD_COMPLETED)
      } catch (err) {
        commit(CHANGE_PASSWORD_FAILED, err)
      }
    }
  }
}
